import React, { useEffect, useRef, useState } from "react"

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)"

// Scroll-reveal: content rises + fades in once as it enters the viewport.
// Uses IntersectionObserver + CSS transitions (no framer dependency), and
// once revealed it clears its inline styles so hover/CSS transitions on the
// child take over cleanly. Respects prefers-reduced-motion.
const Reveal = ({
  children,
  delay = 0,
  y = 30,
  duration = 0.85,
  className,
  style,
}) => {
  const ref = useRef(null)
  const [shown, setShown] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce || !("IntersectionObserver" in window)) {
      setShown(true)
      setDone(true)
      return
    }

    let timer
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShown(true)
          io.disconnect()
          timer = setTimeout(
            () => setDone(true),
            (delay + duration) * 1000 + 80
          )
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    )
    io.observe(el)

    return () => {
      io.disconnect()
      if (timer) clearTimeout(timer)
    }
  }, [delay, duration])

  const animStyle = done
    ? style
    : {
        ...style,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity ${duration}s ${EASE} ${delay}s, transform ${duration}s ${EASE} ${delay}s`,
        willChange: "opacity, transform",
      }

  return (
    <div ref={ref} className={className} style={animStyle}>
      {children}
    </div>
  )
}

export default Reveal
